import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import Translate from '@hydra/common/components/i18n';
import Icon from '@hydra/common/components/Icon';
import Flag from '@hydra/common/components/Flag';
import WorldMap from '../components/WorldMap';
import CriteriumTable from '@hydra/common/criterium/CriteriumTable';
import Criterium from '@hydra/common/criterium/Criterium';
import SafeAnchor from '@hydra/common/components/SafeAnchor';
import tldToCountryCode from '@hydra/utils/tldToCountryCode';
import getWooIndexLink from '@hydra/utils/getWooIndexLink';
import { useReviewContext } from '@hydra/common/context/review';

const CCTLD_LINK = 'https://en.wikipedia.org/wiki/Country_code_top-level_domain';
const GO_DADDY_LINK = 'http://bit.ly/1qKTAle';

const useStyles = makeStyles({
  link: { verticalAlign: 'middle' }
});

export const VisitorsLoc = ({ criterium, gaAuthenticated }) => {
  const { isPdf } = useReviewContext();
  const classes = useStyles();
  const { data } = criterium;

  if (gaAuthenticated) {
    return null;
  }

  const hasMap = data.popularityMap && data.popularityMap.length > 0;
  const hasTable = data.top5CountryExtensions && data.top5CountryExtensions.length > 0;

  const headings = [
    <Translate content='suggested_domains' key='suggested_domains' />,
    <SafeAnchor href={CCTLD_LINK} rel='nofollow noopener noreferrer' target='_blank' key='cctld'>
      <Translate content='cctld' />
    </SafeAnchor>,
    <Translate content='status' key='status' />,
    <span key='nbsp'>&nbsp;</span>
  ];

  const rows = hasTable && data.top5CountryExtensions.map(extension => {
    const countryCode = tldToCountryCode(extension.domain.split('.').pop());
    const cctldText = extension.isAvailable
      ? (
        <span>
          <Translate content='domain_available' />
          {' '}
          <SafeAnchor href={GO_DADDY_LINK} rel='nofollow noopener noreferrer' target='_blank'><Translate content='eurdns_book_it' /></SafeAnchor>
        </span>
        )
      : <Translate content='domain_booked' />;

    const icon = extension.isAvailable
      ? <Icon type='woorank-success' color='success' size='xs' />
      : <Icon type='lock' color='grey' size='xs' />;

    return [
      <React.Fragment key='flag'>
        <Flag country={countryCode} />
        &nbsp;
        {
          isPdf
            ? <Translate content={`country_name_${countryCode.toUpperCase()}`} />
            : (
              <SafeAnchor href={getWooIndexLink(countryCode)} rel='nofollow noopener noreferrer' target='_blank' className={classes.link}>
                <Translate content={`country_name_${countryCode.toUpperCase()}`} />
              </SafeAnchor>
              )
        }
      </React.Fragment>,
      <span key='domain'>{extension.domain}</span>,
      cctldText,
      icon
    ];
  });

  const tableClasses = {
    head: ['', '', '', 'alignRight'],
    body: ['', '', '', 'alignRight']
  };

  const content = (
    <>
      {
        hasMap
          ? <WorldMap highlights={data.popularityMap} />
          : null
      }
      {
        hasTable
          ? (
            <CriteriumTable
              head={headings}
              rows={rows}
              fixedColumns={[4, 2, 5, 1]}
              classNames={tableClasses}
              thinHead
              striped
              nowrap
            />
            )
          : null
      }
    </>
  );

  return <Criterium criterium={criterium}>{content}</Criterium>;
};

VisitorsLoc.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      popularityMap: PropTypes.arrayOf(
        PropTypes.shape({
          country: PropTypes.string,
          value: PropTypes.number
        })
      ),
      top5CountryExtensions: PropTypes.arrayOf(
        PropTypes.shape({
          domain: PropTypes.string,
          isAvailable: PropTypes.bool
        })
      )
    })
  })
};

export default React.memo(VisitorsLoc);
